import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import AddressInput, { AddressLink } from '../components/AddressInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
    Ticket, Star, Sparkles, Shield, Clock, Tag,
    Plus, Edit3, Trash2, X, Upload, Search, Filter,
    Utensils, Dumbbell, Laptop, Shirt, Scissors, Film, Plane, Gift,
    QrCode, Download, ScanLine, Users, BarChart3, RefreshCw, History
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import BenefitScanner from '../components/BenefitScanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category config with icons and colors
const categoryConfig = {
    food: { icon: Utensils, label: 'Food & Drinks', color: 'from-orange-500 to-red-500' },
    fitness: { icon: Dumbbell, label: 'Fitness & Sport', color: 'from-green-500 to-emerald-500' },
    tech: { icon: Laptop, label: 'Technik', color: 'from-blue-500 to-cyan-500' },
    fashion: { icon: Shirt, label: 'Fashion', color: 'from-pink-500 to-purple-500' },
    beauty: { icon: Scissors, label: 'Beauty & Wellness', color: 'from-rose-400 to-pink-500' },
    entertainment: { icon: Film, label: 'Entertainment', color: 'from-violet-500 to-purple-500' },
    travel: { icon: Plane, label: 'Reisen', color: 'from-sky-500 to-blue-500' },
    other: { icon: Gift, label: 'Sonstiges', color: 'from-gray-500 to-slate-500' }
};

// Floating particles component
const FloatingParticles = () => {
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(20)].map((_, i) => (
                <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-primary/30 rounded-full"
                    initial={{
                        x: Math.random() * window.innerWidth,
                        y: Math.random() * window.innerHeight,
                        scale: Math.random() * 0.5 + 0.5,
                        opacity: Math.random() * 0.5 + 0.3
                    }}
                    animate={{
                        y: [null, Math.random() * -200 - 100],
                        opacity: [null, 0]
                    }}
                    transition={{
                        duration: Math.random() * 5 + 5,
                        repeat: Infinity,
                        ease: "linear",
                        delay: Math.random() * 5
                    }}
                />
            ))}
        </div>
    );
};

// Glitter effect component
const GlitterEffect = ({ active }) => {
    if (!active) return null;
    
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(30)].map((_, i) => (
                <motion.div
                    key={i}
                    className="absolute w-1.5 h-1.5 bg-yellow-300 rounded-full"
                    style={{
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        boxShadow: '0 0 6px 2px rgba(253, 224, 71, 0.8)'
                    }}
                    animate={{
                        opacity: [0, 1, 0],
                        scale: [0, 1.5, 0]
                    }}
                    transition={{
                        duration: Math.random() * 2 + 1,
                        repeat: Infinity,
                        delay: Math.random() * 2
                    }}
                />
            ))}
        </div>
    );
};

// Premium Benefit Card with VIP effects
const BenefitCard = ({ benefit, onClick, index }) => {
    const [isHovered, setIsHovered] = useState(false);
    const CategoryIcon = categoryConfig[benefit.category]?.icon || Gift;
    const categoryColor = categoryConfig[benefit.category]?.color || 'from-gray-500 to-slate-500';

    return (
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: index * 0.05, duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
            whileHover={{ scale: 1.02, y: -4 }}
            whileTap={{ scale: 0.98 }}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            onClick={onClick}
            className="relative cursor-pointer group"
            data-testid={`benefit-card-${benefit.id}`}
        >
            {/* Glow effect on hover */}
            <motion.div
                className={`absolute -inset-0.5 bg-gradient-to-r ${categoryColor} rounded-[20px] opacity-0 blur-md transition-opacity duration-300`}
                animate={{ opacity: isHovered ? 0.6 : 0 }}
            />
            
            {/* Animated border */}
            <motion.div
                className="absolute inset-0 rounded-[18px] overflow-hidden"
                style={{
                    background: isHovered 
                        ? `linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)`
                        : 'transparent',
                    backgroundSize: '200% 100%'
                }}
                animate={{
                    backgroundPosition: isHovered ? ['200% 0', '-200% 0'] : '0% 0'
                }}
                transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
            />

            <Card className={`relative overflow-hidden bg-card/95 backdrop-blur-xl border-2 transition-all duration-300 ${
                isHovered ? 'border-primary/50 shadow-2xl' : 'border-border/50 shadow-lg'
            }`} style={{ borderRadius: '18px' }}>
                {/* Shine effect overlay */}
                <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full"
                    animate={{ x: isHovered ? '200%' : '-100%' }}
                    transition={{ duration: 0.8, ease: 'easeInOut' }}
                />

                <CardContent className="p-5 relative">
                    {/* Glitter effect when hovered */}
                    <GlitterEffect active={isHovered} />

                    {/* Top row: Logo + Name + Discount */}
                    <div className="flex items-center gap-3">
                        {/* Logo with glow */}
                        <div className="relative shrink-0">
                            <motion.div
                                className={`absolute -inset-2 bg-gradient-to-r ${categoryColor} rounded-2xl opacity-0 blur-lg`}
                                animate={{ opacity: isHovered ? 0.4 : 0 }}
                            />
                            <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-muted to-muted/50 flex items-center justify-center overflow-hidden shadow-inner">
                                {benefit.logo_url ? (
                                    <img src={benefit.logo_url} alt={benefit.name} className="w-full h-full object-cover" />
                                ) : (
                                    <CategoryIcon className="w-7 h-7 text-muted-foreground" />
                                )}
                            </div>
                        </div>

                        {/* Name + Category */}
                        <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-base leading-tight group-hover:text-primary transition-colors line-clamp-2">
                                {benefit.name}
                            </h3>
                            <Badge variant="secondary" className="text-xs mt-1">
                                <CategoryIcon className="w-3 h-3 mr-1" />
                                {categoryConfig[benefit.category]?.label || 'Sonstiges'}
                            </Badge>
                        </div>

                        {/* DISCOUNT BADGE */}
                        <motion.div
                            className="relative shrink-0"
                            animate={{ 
                                scale: isHovered ? [1, 1.1, 1] : 1,
                                rotate: isHovered ? [0, -3, 3, 0] : 0
                            }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className={`relative px-3 py-1.5 rounded-xl bg-gradient-to-br ${categoryColor} shadow-lg`}>
                                <Sparkles className="absolute -top-1 -right-1 w-3.5 h-3.5 text-yellow-300 animate-pulse" />
                                <span className="text-white font-black text-lg tracking-tight">
                                    {benefit.discount}
                                </span>
                            </div>
                        </motion.div>
                    </div>

                    {/* Location - own row */}
                    {benefit.location && (
                        <div className="flex items-center gap-1 mt-2.5 text-xs text-muted-foreground min-w-0">
                            <AddressLink address={benefit.location} className="min-w-0 hover:text-primary" />
                        </div>
                    )}

                    {/* Description preview */}
                    {benefit.description && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                            {benefit.description}
                        </p>
                    )}

                    {/* Footer badges */}
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        {benefit.valid_until && (
                            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                Bis {format(new Date(benefit.valid_until), 'd. MMM yyyy', { locale: de })}
                            </span>
                        )}
                        {benefit.work_hours_only && (
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 dark:text-blue-400">
                                <Clock className="w-3 h-3" />
                                Arbeitszeit
                            </span>
                        )}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};

// VIP Vorzeige-Modal
const BenefitShowModal = ({ benefit, user, onClose, onEdit, onScan }) => {
    const [currentTime, setCurrentTime] = useState(new Date());
    const CategoryIcon = categoryConfig[benefit.category]?.icon || Gift;
    const categoryColor = categoryConfig[benefit.category]?.color || 'from-gray-500 to-slate-500';

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    const getInitials = (first, last) => {
        return `${first?.[0] || ''}${last?.[0] || ''}`.toUpperCase();
    };

    // Lock body scroll and hide fixed elements when modal is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        // Hide header bubble on mobile
        const headerBubble = document.querySelector('[data-testid="header-bubble-mobile"]');
        if (headerBubble) {
            headerBubble.style.display = 'none';
        }
        return () => {
            document.body.style.overflow = '';
            if (headerBubble) {
                headerBubble.style.display = '';
            }
        };
    }, []);

    return ReactDOM.createPortal(
        <>
            {/* Full screen overlay */}
            <div 
                className="fixed inset-0 bg-black/95 z-[9998]" 
                onClick={onClose}
            />
            <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pb-20">
                <motion.div 
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="relative w-full max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-3xl shadow-2xl"
                    data-testid="benefit-show-modal"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close button */}
                    <button 
                        onClick={onClose}
                        className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                    >
                        <X className="w-4 h-4 text-white" />
                    </button>
                {/* Smooth floating particles - reduced count, CSS animation for better mobile performance */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {[...Array(15)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute rounded-full animate-float-up"
                            style={{
                                width: Math.random() * 3 + 2,
                                height: Math.random() * 3 + 2,
                                left: `${Math.random() * 100}%`,
                                bottom: `-${Math.random() * 20}%`,
                                background: i % 3 === 0 ? 'rgba(253, 224, 71, 0.6)' : 'rgba(255, 255, 255, 0.4)',
                                animationDelay: `${Math.random() * 5}s`,
                                animationDuration: `${Math.random() * 4 + 6}s`
                            }}
                        />
                    ))}
                </div>

                {/* VIP Header with glow */}
                <div className="relative p-6 pt-8 text-center">
                    {/* Glow behind logo */}
                    <motion.div
                        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-gradient-to-r ${categoryColor} rounded-full blur-3xl opacity-30`}
                        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    />
                    
                    {/* Badges row - properly spaced */}
                    <div className="absolute top-3 left-0 right-0 flex items-center justify-between px-4">
                        {/* Active Badge - left */}
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-500/20 border border-green-500/50 text-green-400 text-xs font-medium">
                                <motion.div
                                    className="w-2 h-2 rounded-full bg-green-400"
                                    animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
                                    transition={{ duration: 1.5, repeat: Infinity }}
                                />
                                AKTIV
                            </div>
                        </motion.div>

                        {/* VIP Badge - right, away from X button */}
                        <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ type: 'spring', bounce: 0.5, delay: 0.2 }}
                            className="mr-8"
                        >
                            <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-primary to-blue-600 text-white text-xs font-bold shadow-lg">
                                <Star className="w-3 h-3" />
                                VIP
                            </div>
                        </motion.div>
                    </div>

                    {/* Logo */}
                    <motion.div
                        initial={{ scale: 0, y: 20 }}
                        animate={{ scale: 1, y: 0 }}
                        transition={{ type: 'spring', bounce: 0.4, delay: 0.1 }}
                        className="relative w-24 h-24 mx-auto rounded-2xl bg-white shadow-2xl overflow-hidden mb-4"
                    >
                        {benefit.logo_url ? (
                            <img src={benefit.logo_url} alt={benefit.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className={`w-full h-full bg-gradient-to-br ${categoryColor} flex items-center justify-center`}>
                                <CategoryIcon className="w-12 h-12 text-white" />
                            </div>
                        )}
                    </motion.div>

                    {/* Name */}
                    <motion.h2
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-2xl font-black text-white tracking-tight"
                    >
                        {benefit.name.toUpperCase()}
                    </motion.h2>

                    {/* DISCOUNT - Hero Element */}
                    <motion.div
                        initial={{ scale: 0, rotate: -10 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ type: 'spring', bounce: 0.5, delay: 0.3 }}
                        className="relative mt-4"
                    >
                        {/* Glow */}
                        <div className={`absolute inset-0 bg-gradient-to-r ${categoryColor} blur-2xl opacity-50`} />
                        
                        <div className={`relative inline-block px-8 py-4 rounded-2xl bg-gradient-to-br ${categoryColor} shadow-2xl`}>
                            {/* Sparkles */}
                            <Sparkles className="absolute -top-2 -left-2 w-6 h-6 text-yellow-300 animate-pulse" />
                            <Sparkles className="absolute -bottom-2 -right-2 w-5 h-5 text-yellow-300 animate-pulse" style={{ animationDelay: '0.5s' }} />
                            
                            <span className="text-white font-black text-5xl tracking-tighter">
                                {benefit.discount}
                            </span>
                        </div>
                    </motion.div>

                    {/* Description */}
                    {benefit.description && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.4 }}
                            className="mt-4 text-gray-300 text-sm leading-relaxed"
                        >
                            {benefit.description}
                        </motion.p>
                    )}

                    {/* Info pills: Location, Category, Work hours */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.45 }}
                        className="flex flex-wrap items-center justify-center gap-2 mt-4"
                    >
                        {benefit.location && (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-gray-300 text-xs">
                                <AddressLink address={benefit.location} />
                            </div>
                        )}
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-gray-300 text-xs">
                            <CategoryIcon className="w-3 h-3" />
                            {categoryConfig[benefit.category]?.label || 'Sonstiges'}
                        </div>
                        {benefit.work_hours_only && (
                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-300 text-xs">
                                <Clock className="w-3 h-3" />
                                Nur Arbeitszeit
                            </div>
                        )}
                    </motion.div>

                    {/* Redemption note */}
                    {benefit.redemption_note && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.48 }}
                            className="mt-4 mx-auto max-w-xs p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30"
                        >
                            <p className="text-yellow-300 text-xs font-medium text-center">
                                {benefit.redemption_note}
                            </p>
                        </motion.div>
                    )}

                    {/* Terms */}
                    {benefit.terms && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.5 }}
                            className="mt-3 text-gray-400 text-xs italic text-center"
                        >
                            {benefit.terms}
                        </motion.p>
                    )}

                    {/* Validity */}
                    {benefit.valid_until && (
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.52 }}
                            className="mt-2 text-gray-500 text-xs text-center"
                        >
                            Gültig bis {format(new Date(benefit.valid_until), 'd. MMMM yyyy', { locale: de })}
                        </motion.p>
                    )}
                </div>

                {/* Divider with glow */}
                <div className="relative h-px mx-6">
                    <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-primary/50 to-transparent`} />
                </div>

                {/* Employee Verification Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="relative p-6"
                >
                    <div className="flex items-center gap-4 p-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm">
                        {/* Profile Image */}
                        <Avatar className="w-14 h-14 ring-2 ring-primary/50">
                            {user?.profile_image ? (
                                <AvatarImage src={user.profile_image} />
                            ) : (
                                <AvatarFallback 
                                    style={{ backgroundColor: user?.profile_color || '#009dde' }}
                                    className="text-white text-lg font-bold"
                                >
                                    {getInitials(user?.first_name, user?.last_name)}
                                </AvatarFallback>
                            )}
                        </Avatar>

                        <div className="flex-1">
                            <p className="text-white font-semibold text-lg">
                                {user?.first_name} {user?.last_name}
                            </p>
                            <p className="text-gray-400 text-sm capitalize">
                                {user?.display_title || (user?.role === 'chef' ? 'Geschäftsführer' : 
                                 user?.role === 'account_manager' ? 'Account Manager' : 
                                 user?.role === 'mitarbeiter' ? 'Mitarbeiter' : user?.role)}
                            </p>
                        </div>

                        {/* Verified badge */}
                        <div className="flex flex-col items-end gap-1">
                            <div className="flex items-center gap-1 text-green-400 text-xs">
                                <Shield className="w-4 h-4" />
                                Verifiziert
                            </div>
                        </div>
                    </div>

                    {/* Live timestamp */}
                    <div className="flex items-center justify-center gap-2 mt-4 text-gray-400 text-sm">
                        <motion.div
                            className="w-2 h-2 rounded-full bg-red-500"
                            animate={{ opacity: [1, 0.3, 1] }}
                            transition={{ duration: 1, repeat: Infinity }}
                        />
                        <span>LIVE</span>
                        <span className="font-mono">
                            {format(currentTime, 'dd.MM.yyyy • HH:mm:ss')}
                        </span>
                    </div>

                    {/* Disclaimer */}
                    <p className="mt-4 text-center text-gray-500 text-xs">
                        Nur gültig in Verbindung mit dem digitalen Mitarbeiter-Nachweis.
                    </p>
                </motion.div>

                {/* Close Button */}
                <div className="p-6 pt-0 space-y-2">
                    {/* QR Scanner Button - Main CTA */}
                    {benefit.requires_qr_scan && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <Button 
                                onClick={() => { onClose(); onScan?.(benefit); }} 
                                className={`w-full h-14 text-lg font-bold rounded-2xl bg-gradient-to-r ${categoryColor} hover:opacity-90 text-white shadow-lg transition-all`}
                                data-testid="benefit-modal-scan"
                            >
                                <ScanLine className="w-5 h-5 mr-2" />
                                Jetzt scannen & einlösen
                            </Button>
                        </motion.div>
                    )}
                    {onEdit && (
                        <Button 
                            onClick={() => { onClose(); onEdit(benefit); }} 
                            className="w-full h-12 text-base font-semibold rounded-2xl bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 transition-all"
                            data-testid="benefit-modal-edit"
                        >
                            <Edit3 className="w-4 h-4 mr-2" />
                            Bearbeiten
                        </Button>
                    )}
                    <Button 
                        onClick={onClose} 
                        className="w-full h-12 text-base font-semibold rounded-2xl bg-white/10 hover:bg-white/20 text-white border border-white/20 transition-all"
                        data-testid="benefit-modal-close"
                    >
                        Schließen
                    </Button>
                </div>
                </motion.div>
            </div>
        </>,
        document.body
    );
};

// Admin Create/Edit Modal
const BenefitEditModal = ({ benefit, onSave, onClose, api }) => {
    const [form, setForm] = useState({
        name: benefit?.name || '',
        discount: benefit?.discount || '',
        description: benefit?.description || '',
        category: benefit?.category || 'other',
        location: benefit?.location || '',
        valid_from: benefit?.valid_from || '',
        valid_until: benefit?.valid_until || '',
        is_active: benefit?.is_active ?? true,
        terms: benefit?.terms || '',
        work_hours_only: benefit?.work_hours_only || false,
        redemption_note: benefit?.redemption_note || '',
        requires_qr_scan: benefit?.requires_qr_scan || false,
        max_redemptions_per_user: benefit?.max_redemptions_per_user || null
    });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(benefit?.logo_url || null);
    const [saving, setSaving] = useState(false);

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleSave = async () => {
        if (!form.name || !form.discount) {
            toast.error('Name und Rabatt sind erforderlich');
            return;
        }

        setSaving(true);
        try {
            let savedBenefit;
            
            if (benefit?.id) {
                // Update existing
                const response = await api.put(`/benefits/${benefit.id}`, form);
                savedBenefit = response.data;
            } else {
                // Create new
                const response = await api.post('/benefits', form);
                savedBenefit = response.data;
            }

            // Upload logo if selected
            if (logoFile && savedBenefit.id) {
                const formData = new FormData();
                formData.append('file', logoFile);
                await api.post(`/benefits/${savedBenefit.id}/logo`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            toast.success(benefit?.id ? 'Vorteil aktualisiert' : 'Vorteil erstellt');
            onSave();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Ticket className="w-5 h-5" />
                        {benefit?.id ? 'Vorteil bearbeiten' : 'Neuer Vorteil'}
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* Logo Upload */}
                    <div>
                        <Label>Logo</Label>
                        <div className="mt-2 flex items-center gap-4">
                            <div className="w-20 h-20 rounded-xl bg-muted flex items-center justify-center overflow-hidden border-2 border-dashed border-border">
                                {logoPreview ? (
                                    <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                                ) : (
                                    <Upload className="w-8 h-8 text-muted-foreground" />
                                )}
                            </div>
                            <div>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoChange}
                                    className="hidden"
                                    id="logo-upload"
                                />
                                <Button variant="outline" size="sm" asChild>
                                    <label htmlFor="logo-upload" className="cursor-pointer">
                                        <Upload className="w-4 h-4 mr-2" />
                                        Logo hochladen
                                    </label>
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Name */}
                    <div>
                        <Label>Name *</Label>
                        <Input
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="z.B. Dönermeister"
                            className="mt-1"
                        />
                    </div>

                    {/* Discount */}
                    <div>
                        <Label>Rabatt *</Label>
                        <Input
                            value={form.discount}
                            onChange={(e) => setForm({ ...form, discount: e.target.value })}
                            placeholder="z.B. -50%, 2 für 1, 10€"
                            className="mt-1 text-xl font-bold"
                        />
                    </div>

                    {/* Category */}
                    <div>
                        <Label>Kategorie</Label>
                        <Select value={form.category} onValueChange={(val) => setForm({ ...form, category: val })}>
                            <SelectTrigger className="mt-1">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {Object.entries(categoryConfig).map(([key, { icon: Icon, label }]) => (
                                    <SelectItem key={key} value={key}>
                                        <div className="flex items-center gap-2">
                                            <Icon className="w-4 h-4" />
                                            {label}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Location */}
                    <div>
                        <Label>Standort (optional)</Label>
                        <AddressInput
                            value={form.location}
                            onChange={(v) => setForm({ ...form, location: v })}
                            placeholder="z.B. Wien, 1010"
                            className="mt-1"
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <Label>Beschreibung (optional)</Label>
                        <Textarea
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Kurze Beschreibung des Vorteils..."
                            className="mt-1"
                            rows={2}
                        />
                    </div>

                    {/* Terms */}
                    <div>
                        <Label>Einlösebedingungen (optional)</Label>
                        <Textarea
                            value={form.terms}
                            onChange={(e) => setForm({ ...form, terms: e.target.value })}
                            placeholder="z.B. Gültig auf alle Speisen, außer Getränke."
                            className="mt-1"
                            rows={2}
                        />
                    </div>

                    {/* Work hours only */}
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/30 border border-border/40">
                        <Checkbox
                            id="work-hours-only"
                            checked={form.work_hours_only || false}
                            onCheckedChange={(checked) => setForm({ ...form, work_hours_only: checked })}
                            data-testid="work-hours-only"
                        />
                        <label htmlFor="work-hours-only" className="text-sm cursor-pointer">
                            Nur während der Arbeitszeit einlösbar
                        </label>
                    </div>

                    {/* Redemption note */}
                    <div>
                        <Label>Was muss man sagen/vorzeigen? (optional)</Label>
                        <Input
                            value={form.redemption_note || ''}
                            onChange={(e) => setForm({ ...form, redemption_note: e.target.value })}
                            placeholder="z.B. Kontaktname: Frau Müller, Rabattcode: TEAM2026"
                            className="mt-1"
                            data-testid="redemption-note"
                        />
                    </div>

                    {/* QR Code Scanning Section */}
                    <div className="p-4 rounded-xl bg-primary/5 border border-primary/20 space-y-4">
                        <div className="flex items-center gap-2 text-primary">
                            <QrCode className="w-5 h-5" />
                            <span className="font-medium">QR-Code Einlösung</span>
                        </div>
                        
                        {/* Requires QR Scan */}
                        <div className="flex items-center gap-3">
                            <Checkbox
                                id="requires-qr-scan"
                                checked={form.requires_qr_scan || false}
                                onCheckedChange={(checked) => setForm({ ...form, requires_qr_scan: checked })}
                                data-testid="requires-qr-scan"
                            />
                            <label htmlFor="requires-qr-scan" className="text-sm cursor-pointer">
                                QR-Code Scan erforderlich zum Einlösen
                            </label>
                        </div>

                        {/* Max redemptions per user */}
                        {form.requires_qr_scan && (
                            <div>
                                <Label className="text-sm">Max. Einlösungen pro Mitarbeiter</Label>
                                <div className="flex items-center gap-3 mt-2">
                                    <Input
                                        type="number"
                                        min="1"
                                        value={form.max_redemptions_per_user || ''}
                                        onChange={(e) => setForm({ 
                                            ...form, 
                                            max_redemptions_per_user: e.target.value ? parseInt(e.target.value) : null 
                                        })}
                                        placeholder="Unbegrenzt"
                                        className="w-32"
                                        data-testid="max-redemptions"
                                    />
                                    <span className="text-sm text-muted-foreground">
                                        Leer lassen für unbegrenzt
                                    </span>
                                </div>
                            </div>
                        )}

                        <p className="text-xs text-muted-foreground">
                            Bei aktiviertem QR-Scan wird ein eindeutiger QR-Code generiert, den du beim Kunden ausdrucken kannst.
                        </p>
                    </div>

                    {/* Validity dates */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Gültig ab</Label>
                            <Input
                                type="date"
                                value={form.valid_from}
                                onChange={(e) => setForm({ ...form, valid_from: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <Label>Gültig bis</Label>
                            <Input
                                type="date"
                                value={form.valid_until}
                                onChange={(e) => setForm({ ...form, valid_until: e.target.value })}
                                className="mt-1"
                            />
                        </div>
                    </div>

                    {/* Active toggle */}
                    <div className="flex items-center justify-between py-2">
                        <div>
                            <Label>Aktiv</Label>
                            <p className="text-xs text-muted-foreground">Für Mitarbeiter sichtbar</p>
                        </div>
                        <Switch
                            checked={form.is_active}
                            onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                        />
                    </div>
                </div>

                <DialogFooter className="mt-4">
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={handleSave} disabled={saving}>
                        {saving ? 'Speichern...' : (benefit?.id ? 'Speichern' : 'Erstellen')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

// Main Page Component
const BenefitsPage = () => {
    const { user, api } = useAuth();
    const [benefits, setBenefits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [selectedBenefit, setSelectedBenefit] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingBenefit, setEditingBenefit] = useState(null);
    const [showScanner, setShowScanner] = useState(false);
    const [scanningBenefit, setScanningBenefit] = useState(null);
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrBenefit, setQRBenefit] = useState(null);
    const [showRedemptionsModal, setShowRedemptionsModal] = useState(false);
    const [redemptionsBenefit, setRedemptionsBenefit] = useState(null);

    const isAdmin = user?.role === 'chef';

    const fetchBenefits = useCallback(async () => {
        try {
            const endpoint = isAdmin ? '/benefits/all' : '/benefits';
            const response = await api.get(endpoint);
            setBenefits(response.data);
        } catch (error) {
            console.error('Failed to fetch benefits:', error);
            toast.error('Fehler beim Laden der Vorteile');
        } finally {
            setLoading(false);
        }
    }, [api, isAdmin]);

    useEffect(() => {
        fetchBenefits();
    }, [fetchBenefits]);

    const handleDelete = async (benefitId) => {
        if (!window.confirm('Vorteil wirklich löschen?')) return;
        
        try {
            await api.delete(`/benefits/${benefitId}`);
            toast.success('Vorteil gelöscht');
            fetchBenefits();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const filteredBenefits = benefits.filter(b => {
        const matchesSearch = b.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                             b.description?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || b.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                    className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full"
                />
            </div>
        );
    }

    return (
        <div className="relative min-h-screen p-4 md:p-6 lg:p-8 overflow-hidden" data-testid="benefits-page">
            {/* Floating particles background */}
            <FloatingParticles />

            {/* Ambient glow */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative max-w-6xl mx-auto">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center shadow-lg">
                            <Ticket className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-black tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Vorteilspass
                                <Sparkles className="w-6 h-6 text-yellow-500 animate-pulse" />
                            </h1>
                            <p className="text-sm text-muted-foreground">Exklusive Mitarbeiter-Vorteile</p>
                        </div>
                    </div>
                    
                    {/* Info text */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="mt-4 p-4 rounded-xl bg-muted/50 border border-border/50 backdrop-blur-sm"
                    >
                        <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Shield className="w-4 h-4 text-primary" />
                            Mit Vorzeigen des jeweiligen Rabatts einlösbar. Nur gültig in Verbindung mit dem digitalen Mitarbeiter-Nachweis.
                        </p>
                    </motion.div>
                </motion.div>

                {/* Search & Filter Bar */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="flex flex-wrap gap-3 mb-6"
                >
                    <div className="relative flex-1 min-w-[200px]">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Suchen..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                        />
                    </div>

                    <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                        <SelectTrigger className="w-[180px]">
                            <Filter className="w-4 h-4 mr-2" />
                            <SelectValue placeholder="Kategorie" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Alle Kategorien</SelectItem>
                            {Object.entries(categoryConfig).map(([key, { icon: Icon, label }]) => (
                                <SelectItem key={key} value={key}>
                                    <div className="flex items-center gap-2">
                                        <Icon className="w-4 h-4" />
                                        {label}
                                    </div>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {isAdmin && (
                        <Button onClick={() => { setEditingBenefit(null); setShowEditModal(true); }}>
                            <Plus className="w-4 h-4 mr-2" />
                            Neuer Vorteil
                        </Button>
                    )}
                </motion.div>

                {/* Benefits Grid */}
                {filteredBenefits.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center py-16"
                    >
                        <Ticket className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                        <h3 className="text-xl font-semibold text-muted-foreground">Keine Vorteile gefunden</h3>
                        <p className="text-muted-foreground mt-1">
                            {searchQuery || selectedCategory !== 'all' 
                                ? 'Versuche eine andere Suche oder Kategorie'
                                : 'Es wurden noch keine Vorteile erstellt'}
                        </p>
                    </motion.div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {filteredBenefits.map((benefit, index) => (
                            <div key={benefit.id} className="relative group">
                                <BenefitCard
                                    benefit={benefit}
                                    index={index}
                                    onClick={() => setSelectedBenefit(benefit)}
                                />
                                
                                {/* Admin actions */}
                                {isAdmin && (
                                    <div className="absolute bottom-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                        {/* QR Code button - only if requires_qr_scan */}
                                        {benefit.requires_qr_scan && (
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                className="h-8 w-8 p-0 bg-primary/90 hover:bg-primary text-white shadow-sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setQRBenefit(benefit);
                                                    setShowQRModal(true);
                                                }}
                                                data-testid={`qr-benefit-${benefit.id}`}
                                                title="QR-Code anzeigen"
                                            >
                                                <QrCode className="w-3.5 h-3.5" />
                                            </Button>
                                        )}
                                        {/* Redemptions button - only if requires_qr_scan */}
                                        {benefit.requires_qr_scan && benefit.total_redemptions > 0 && (
                                            <Button
                                                size="sm"
                                                variant="secondary"
                                                className="h-8 px-2 bg-white/90 hover:bg-white shadow-sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setRedemptionsBenefit(benefit);
                                                    setShowRedemptionsModal(true);
                                                }}
                                                data-testid={`redemptions-benefit-${benefit.id}`}
                                                title="Einlösungen anzeigen"
                                            >
                                                <History className="w-3.5 h-3.5 text-gray-700 mr-1" />
                                                <span className="text-xs font-bold text-gray-700">{benefit.total_redemptions}</span>
                                            </Button>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="secondary"
                                            className="h-8 w-8 p-0 bg-white/90 hover:bg-white shadow-sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setEditingBenefit(benefit);
                                                setShowEditModal(true);
                                            }}
                                            data-testid={`edit-benefit-${benefit.id}`}
                                        >
                                            <Edit3 className="w-3.5 h-3.5 text-gray-700" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            className="h-8 w-8 p-0 shadow-sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(benefit.id);
                                            }}
                                            data-testid={`delete-benefit-${benefit.id}`}
                                        >
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </Button>
                                    </div>
                                )}
                                
                                {/* Inactive badge for admin */}
                                {isAdmin && !benefit.is_active && (
                                    <Badge variant="secondary" className="absolute top-2 left-2">
                                        Inaktiv
                                    </Badge>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Show Modal */}
            <AnimatePresence>
                {selectedBenefit && (
                    <BenefitShowModal
                        benefit={selectedBenefit}
                        user={user}
                        onClose={() => setSelectedBenefit(null)}
                        onEdit={isAdmin ? (b) => {
                            setEditingBenefit(b);
                            setShowEditModal(true);
                        } : undefined}
                        onScan={(b) => {
                            setScanningBenefit(b);
                            setShowScanner(true);
                        }}
                    />
                )}
            </AnimatePresence>

            {/* Edit Modal */}
            {showEditModal && (
                <BenefitEditModal
                    benefit={editingBenefit}
                    api={api}
                    onSave={() => {
                        setShowEditModal(false);
                        setEditingBenefit(null);
                        fetchBenefits();
                    }}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingBenefit(null);
                    }}
                />
            )}

            {/* QR Scanner */}
            {showScanner && (
                <BenefitScanner
                    benefit={scanningBenefit}
                    onClose={() => {
                        setShowScanner(false);
                        setScanningBenefit(null);
                    }}
                    onSuccess={() => {
                        fetchBenefits();
                    }}
                />
            )}

            {/* QR Code Modal for Admin */}
            {showQRModal && qrBenefit && (
                <QRCodeModal
                    benefit={qrBenefit}
                    api={api}
                    onClose={() => {
                        setShowQRModal(false);
                        setQRBenefit(null);
                    }}
                />
            )}

            {/* Redemptions Modal for Admin */}
            {showRedemptionsModal && redemptionsBenefit && (
                <RedemptionsModal
                    benefit={redemptionsBenefit}
                    api={api}
                    onClose={() => {
                        setShowRedemptionsModal(false);
                        setRedemptionsBenefit(null);
                    }}
                />
            )}
        </div>
    );
};

// QR Code Modal Component for Admin
const QRCodeModal = ({ benefit, api, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [qrImageUrl, setQrImageUrl] = useState(null);

    useEffect(() => {
        loadQRCode();
    }, [benefit.id]);

    const loadQRCode = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/benefits/${benefit.id}/qr-code`, {
                responseType: 'blob'
            });
            const url = URL.createObjectURL(response.data);
            setQrImageUrl(url);
        } catch (error) {
            toast.error('QR-Code konnte nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = () => {
        if (qrImageUrl) {
            const link = document.createElement('a');
            link.href = qrImageUrl;
            link.download = `${benefit.name.replace(/\s+/g, '_')}_QR.png`;
            link.click();
            toast.success('QR-Code heruntergeladen');
        }
    };

    const handleRegenerate = async () => {
        if (!window.confirm('QR-Code wirklich neu generieren? Der alte Code wird ungültig!')) return;
        
        try {
            await api.post(`/benefits/${benefit.id}/regenerate-qr`);
            toast.success('Neuer QR-Code generiert');
            loadQRCode();
        } catch (error) {
            toast.error('Fehler beim Regenerieren');
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <QrCode className="w-5 h-5 text-primary" />
                        QR-Code: {benefit.name}
                    </DialogTitle>
                    <DialogDescription>
                        Drucke diesen Code aus und platziere ihn beim Kunden
                    </DialogDescription>
                </DialogHeader>

                <div className="flex flex-col items-center py-6">
                    {loading ? (
                        <div className="w-64 h-64 flex items-center justify-center bg-muted rounded-xl">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full"
                            />
                        </div>
                    ) : qrImageUrl ? (
                        <div className="p-4 bg-white rounded-2xl shadow-lg">
                            <img 
                                src={qrImageUrl} 
                                alt="QR Code" 
                                className="w-64 h-64"
                            />
                        </div>
                    ) : (
                        <div className="w-64 h-64 flex items-center justify-center bg-muted rounded-xl text-muted-foreground">
                            Fehler beim Laden
                        </div>
                    )}

                    <div className="mt-4 text-center">
                        <p className="font-bold text-lg">{benefit.name}</p>
                        <Badge className="mt-1">{benefit.discount}</Badge>
                    </div>
                </div>

                <div className="flex gap-2">
                    <Button onClick={handleDownload} className="flex-1" disabled={!qrImageUrl}>
                        <Download className="w-4 h-4 mr-2" />
                        Herunterladen
                    </Button>
                    <Button onClick={handleRegenerate} variant="outline">
                        <RefreshCw className="w-4 h-4" />
                    </Button>
                </div>

                <p className="text-xs text-muted-foreground text-center mt-2">
                    Beim Regenerieren wird der alte QR-Code ungültig
                </p>
            </DialogContent>
        </Dialog>
    );
};

// Redemptions History Modal for Admin
const RedemptionsModal = ({ benefit, api, onClose }) => {
    const [redemptions, setRedemptions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadRedemptions();
    }, [benefit.id]);

    const loadRedemptions = async () => {
        try {
            const response = await api.get(`/benefits/${benefit.id}/redemptions`);
            setRedemptions(response.data);
        } catch (error) {
            toast.error('Einlösungen konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <History className="w-5 h-5 text-primary" />
                        Einlösungen: {benefit.name}
                    </DialogTitle>
                    <DialogDescription>
                        {redemptions.length} Einlösungen insgesamt
                    </DialogDescription>
                </DialogHeader>

                <ScrollArea className="max-h-[60vh]">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <motion.div
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                                className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full"
                            />
                        </div>
                    ) : redemptions.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <ScanLine className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p>Noch keine Einlösungen</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {redemptions.map((r, i) => (
                                <div 
                                    key={r.id} 
                                    className="flex items-center gap-3 p-3 rounded-lg bg-muted/50"
                                >
                                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                                        {i + 1}
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium">{r.user_name}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {format(new Date(r.redeemed_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
};

export default BenefitsPage;
